import { ApplicationController } from './application_controller.js'

export default class extends ApplicationController {
  static targets = ['sentinel', 'grid', 'selectionCount', 'endMessage', 'saveBar']
  static values = { url: String, userIdsUrl: String, memberIds: Array, allSelected: Boolean }

  connect() {
    this.selectedIds = new Set()
    this.shownIds = new Set()
    this.currentPage = 2
    this.loading = false
    this.observer = null
    this.fetchedAllIds = null

    // Seed selectedIds with every existing member of the list — including
    // members that haven't been rendered into the DOM yet. This keeps the
    // count accurate across scroll batches.
    if (this.hasMemberIdsValue) {
      this.memberIdsValue.forEach(id => this.selectedIds.add(Number(id)))
    }

    // Seed shown from server-rendered user checkboxes in the grid
    this.gridTarget.querySelectorAll('input[name="member_ids[]"]').forEach(cb => {
      this.shownIds.add(Number(cb.value))
    })

    // Seed shown/selected from geo-restrictions panel (sibling panel, same form)
    const geoPanel = this.element.querySelector('[data-geo-shown-ids]')
    if (geoPanel) {
      geoPanel.dataset.geoShownIds.split(',').filter(Boolean).forEach(id => {
        this.shownIds.add(Number(id))
      })
      geoPanel.querySelectorAll('input[name="member_ids[]"]:checked').forEach(cb => {
        this.selectedIds.add(Number(cb.value))
      })
    }

    this.updateSelectionCount()

    const selectAllCb = this.gridTarget.querySelector('input[data-action*="toggleSelectAll"]')
    if (selectAllCb && this.hasAllSelectedValue && this.allSelectedValue) {
      selectAllCb.checked = true
    }

    if (this.hasSentinelTarget) {
      this.observeSentinel(this.sentinelTarget)
    }

    this.onCheckboxChangeBound = this.onCheckboxChange.bind(this)
    this.element.addEventListener('change', this.onCheckboxChangeBound)

    this.onFormSubmitBound = this.onFormSubmit.bind(this)
    const form = document.getElementById('list-members-form')
    if (form) form.addEventListener('submit', this.onFormSubmitBound)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this.element.removeEventListener('change', this.onCheckboxChangeBound)
    const form = document.getElementById('list-members-form')
    if (form) form.removeEventListener('submit', this.onFormSubmitBound)
  }

  observeSentinel(sentinelElement) {
    if (this.observer) this.observer.disconnect()
    this.observer = new IntersectionObserver(
      entries => this.onSentinelIntersect(entries),
      { rootMargin: '200px' }
    )
    this.observer.observe(sentinelElement)
  }

  onSentinelIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.loading) this.loadNextBatch()
    })
  }

  async loadNextBatch() {
    this.loading = true
    if (this.observer) this.observer.disconnect()

    // Remove old sentinel before appending next batch
    const oldSentinel = this.gridTarget.querySelector('[data-infinite-scroll-list-target="sentinel"]')
    if (oldSentinel) oldSentinel.remove()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('page', this.currentPage)
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error(`Batch load failed: ${response.status}`)
      const html = await response.text()

      const temp = document.createElement('div')
      temp.innerHTML = html

      // selectedIds is authoritative. Overwrite any server-rendered checked
      // state — otherwise a freshly-deselected user would come back checked
      // (the server still thinks they're a list member).
      temp.querySelectorAll('input[name="member_ids[]"]').forEach(cb => {
        const id = Number(cb.value)
        this.shownIds.add(id)
        cb.checked = this.selectedIds.has(id)
      })

      while (temp.firstChild) {
        this.gridTarget.appendChild(temp.firstChild)
      }

      this.currentPage++
      this.updateSelectionCount()

      const newSentinel = this.gridTarget.querySelector(
        '[data-infinite-scroll-list-target="sentinel"]'
      )
      if (newSentinel) this.observeSentinel(newSentinel)
    } finally {
      this.loading = false
    }
  }

  async toggleSelectAll(event) {
    const checkbox = event.target
    if (!this.userIdsUrlValue) {
      alert('Unable to toggle select all: no URL configured')
      checkbox.checked = !checkbox.checked
      return
    }
    checkbox.disabled = true
    const originallyChecked = checkbox.checked
    try {
      const ids = await this._fetchAllUserIds()
      if (originallyChecked) {
        this.fetchedAllIds = new Set(ids)
        ids.forEach(id => {
          this.selectedIds.add(id)
          this.shownIds.add(id)
        })
        this.gridTarget.querySelectorAll('input[name="member_ids[]"]').forEach(cb => {
          if (this.selectedIds.has(Number(cb.value))) cb.checked = true
        })
      } else {
        ids.forEach(id => {
          this.selectedIds.delete(id)
          // Include in shownIds so the server knows these are in-scope for
          // removal (update_member_ids only deletes existing members that
          // intersect shown_user_ids).
          this.shownIds.add(id)
        })
        this.fetchedAllIds = null
        this.gridTarget.querySelectorAll('input[name="member_ids[]"]').forEach(cb => {
          cb.checked = false
        })
      }
      this.updateSelectionCount()
    } catch (e) {
      console.error('toggleSelectAll failed', e)
      alert(`Could not ${originallyChecked ? 'select' : 'deselect'} all officials: ${e.message}`)
      checkbox.checked = !originallyChecked
    } finally {
      checkbox.disabled = false
    }
  }

  async _fetchAllUserIds() {
    const url = new URL(this.userIdsUrlValue, window.location.origin)
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()
    const ids = Array.isArray(data && data.ids) ? data.ids.map(Number) : null
    if (!ids) throw new Error('Malformed response: missing ids array')
    return ids
  }

  onCheckboxChange(event) {
    const cb = event.target
    if (cb.name !== 'member_ids[]') return
    const id = Number(cb.value)
    if (cb.checked) {
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }
    if (this.gridTarget.contains(cb)) {
      const selectAllCb = this.gridTarget.querySelector('input[data-action*="toggleSelectAll"]')
      if (selectAllCb) {
        if (cb.checked && !selectAllCb.checked) {
          const allLoaded = !this.gridTarget.querySelector('[data-infinite-scroll-list-target="sentinel"]')
          const memberCheckboxes = [...this.gridTarget.querySelectorAll('input[name="member_ids[]"]')]
          if (allLoaded && memberCheckboxes.length > 0 && memberCheckboxes.every(c => c.checked)) {
            selectAllCb.checked = true
          }
        } else if (!cb.checked && selectAllCb.checked) {
          selectAllCb.checked = false
        }
      }
    }
    this.updateSelectionCount()
  }

  onFormSubmit(event) {
    const form = event.target

    // Remove any fields from a previous submit attempt
    form.querySelectorAll('[data-injected="infinite-scroll-list"]').forEach(el => el.remove())

    // Disable native checkboxes so they don't double-submit, and disable any
    // "select all" master checkboxes (main grid + geo panel) for consistency.
    form.querySelectorAll('input[name="member_ids[]"]').forEach(cb => { cb.disabled = true })
    form.querySelectorAll('input[data-action*="toggleSelectAll"], input[data-action*="toggleAll"]')
      .forEach(cb => { cb.disabled = true })

    // Always inject sentinel '-1' so params[:member_ids] is present (required by update action)
    this._injectHidden(form, 'member_ids[]', '-1')

    // Inject one hidden field per selected user
    this.selectedIds.forEach(id => this._injectHidden(form, 'member_ids[]', id))

    // Inject all shown IDs so update_member_ids knows the displayed scope
    this._injectHidden(form, 'shown_user_ids', Array.from(this.shownIds).join(','))
  }

  updateSelectionCount() {
    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = `${this.selectedIds.size} selected`
    }
  }

  _injectHidden(form, name, value) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    input.dataset.injected = 'infinite-scroll-list'
    form.appendChild(input)
  }
}
